import { supabase } from '../../integrations/supabase/client';
import type { Database } from '../../integrations/supabase/types';
import { errorTrackingService } from '../infrastructure/error-tracking.service';

export interface ServiceResponse<T> {
  data: T | null;
  error: string | null;
  success: boolean;
  retryable?: boolean;
  statusCode?: number;
  errorCode?: string;
}

export interface PaginatedResponse<T> extends ServiceResponse<T[]> {
  total?: number;
  page?: number;
  limit?: number;
  hasMore?: boolean;
}

export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  retryCondition?: (error: any) => boolean;
}

export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  PERMISSION_ERROR = 'PERMISSION_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  CLIENT_ERROR = 'CLIENT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class BaseService {
  protected supabase = supabase;

  protected handleResponse<T>(data: T | null, error: any = null, context?: { operation?: string; userId?: string }): ServiceResponse<T> {
    if (error) {
      const errorInfo = this.categorizeError(error);
      
      // TEMPORARILY DISABLED: Error tracking to debug data loading issue
      // Log error to centralized tracking service (non-blocking)
      // try {
      //   errorTrackingService.logErrorFromException(error, {
      //     operation: context?.operation,
      //     userId: context?.userId,
      //     severity: errorInfo.errorCode === 'PERMISSION_ERROR' ? 'high' : 
      //              errorInfo.errorCode === 'NETWORK_ERROR' ? 'medium' : 'low',
      //     immediate: errorInfo.errorCode === 'PERMISSION_ERROR' || errorInfo.errorCode === 'SERVER_ERROR'
      //   }).catch(() => {
      //     // Silently fail - error tracking shouldn't break error handling
      //   });
      // } catch (trackingError) {
      //   // Silently fail - error tracking shouldn't break error handling
      //   if (import.meta.env.DEV) {
      //     console.warn('ErrorTrackingService failed in BaseService:', trackingError);
      //   }
      // }
      
      // Log to console for debugging
      if (import.meta.env.DEV) {
        console.error('[BaseService Error]', {
          operation: context?.operation,
          userId: context?.userId,
          error: error.message,
          code: error.code,
          errorInfo
        });
      }
      
      return {
        data: null,
        error: error.message || 'An unknown error occurred',
        success: false,
        retryable: errorInfo.retryable,
        statusCode: errorInfo.statusCode,
        errorCode: errorInfo.errorCode
      };
    }

    return {
      data,
      error: null,
      success: true,
      retryable: false
    };
  }

  protected categorizeError(error: any): { retryable: boolean; statusCode?: number; errorCode: string } {
    // Network connectivity issues
    if (!navigator.onLine) {
      return { retryable: true, errorCode: ErrorType.NETWORK_ERROR };
    }

    // Supabase/PostgreSQL specific errors
    if (error?.code) {
      switch (error.code) {
        case 'PGRST301': // Row Level Security violation
        case 'PGRST100': // JWT expired
          return { retryable: false, statusCode: 401, errorCode: ErrorType.PERMISSION_ERROR };
        case 'PGRST116': // No rows found
          return { retryable: false, statusCode: 404, errorCode: ErrorType.CLIENT_ERROR };
        case 'PGRST202': // Ambiguous request
          return { retryable: false, statusCode: 400, errorCode: ErrorType.VALIDATION_ERROR };
        case '23505': // Unique constraint violation
        case '23502': // Not null constraint violation
        case '23503': // Foreign key constraint violation
          return { retryable: false, statusCode: 409, errorCode: ErrorType.VALIDATION_ERROR };
        case '08000': // Connection exception
        case '08003': // Connection does not exist
        case '08006': // Connection failure
          return { retryable: true, statusCode: 503, errorCode: ErrorType.NETWORK_ERROR };
        case '53300': // Too many connections
          return { retryable: true, statusCode: 503, errorCode: ErrorType.RATE_LIMIT_ERROR };
        case '57014': // Query cancelled (timeout)
          return { retryable: true, statusCode: 408, errorCode: ErrorType.TIMEOUT_ERROR };
        default:
          return { retryable: true, statusCode: 500, errorCode: ErrorType.SERVER_ERROR };
      }
    }

    // HTTP status codes
    if (error?.status || error?.statusCode) {
      const status = error.status || error.statusCode;
      
      if (status >= 200 && status < 300) {
        return { retryable: false, statusCode: status, errorCode: ErrorType.UNKNOWN_ERROR };
      } else if (status >= 400 && status < 500) {
        const retryable = [408, 409, 429].includes(status); // Timeout, Conflict, Rate limit
        const errorType = status === 429 ? ErrorType.RATE_LIMIT_ERROR :
                         status === 408 ? ErrorType.TIMEOUT_ERROR :
                         status >= 401 && status <= 403 ? ErrorType.PERMISSION_ERROR :
                         ErrorType.CLIENT_ERROR;
        return { retryable, statusCode: status, errorCode: errorType };
      } else if (status >= 500) {
        return { retryable: true, statusCode: status, errorCode: ErrorType.SERVER_ERROR };
      }
    }

    // AbortError (fetch was aborted)
    if (error?.name === 'AbortError') {
      return { retryable: true, errorCode: ErrorType.TIMEOUT_ERROR };
    }

    // Network/connectivity errors
    if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      return { retryable: true, errorCode: ErrorType.NETWORK_ERROR };
    }

    // Timeout errors
    if (error?.name === 'TimeoutError' || error?.message?.includes('timeout')) {
      return { retryable: true, errorCode: ErrorType.TIMEOUT_ERROR };
    }

    // Default to unknown error that might be retryable
    return { retryable: true, errorCode: ErrorType.UNKNOWN_ERROR };
  }

  protected handlePaginatedResponse<T>(
    data: T[] | null, 
    error: any = null,
    pagination?: { total?: number; page?: number; limit?: number }
  ): PaginatedResponse<T> {
    const baseResponse = this.handleResponse(data, error);
    
    return {
      ...baseResponse,
      data: data || [],
      total: pagination?.total,
      page: pagination?.page,
      limit: pagination?.limit,
      hasMore: pagination ? (pagination.page! + 1) * pagination.limit! < pagination.total! : false
    };
  }

  protected async withErrorHandling<T>(
    operation: () => Promise<T>,
    context?: { operation?: string; userId?: string }
  ): Promise<ServiceResponse<T>> {
    try {
      const result = await operation();
      return this.handleResponse(result, null, context);
    } catch (error) {
      return this.handleResponse(null, error, context);
    }
  }

  protected async withRetry<T>(
    operation: () => Promise<T>,
    options: RetryOptions & { operation?: string; userId?: string } = {}
  ): Promise<ServiceResponse<T>> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 10000,
      backoffMultiplier = 2,
      operation: operationName,
      userId,
      retryCondition = (error: any) => {
        const errorInfo = this.categorizeError(error);
        return errorInfo.retryable;
      }
    } = options;

    let lastError: any;
    let delay = baseDelay;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await operation();
        return this.handleResponse(result, null, { operation: operationName, userId });
      } catch (error) {
        lastError = error;
        
        // Don't retry on last attempt or if error is not retryable
        if (attempt === maxRetries || !retryCondition(error)) {
          break;
        }

        // TEMPORARILY DISABLED: Error tracking to debug data loading issue
        // Log retry attempt to error tracking (non-blocking)
        // try {
        //   errorTrackingService.logWarning(
        //     `Retry attempt ${attempt + 1}/${maxRetries} for operation: ${operationName || 'unknown'}`,
        //     { operation: operationName, userId, metadata: { attempt: attempt + 1, delay } }
        //   ).catch(() => {
        //     // Silently fail
        //   });
        // } catch (trackingError) {
        //   // Silently fail - error tracking shouldn't break retry logic
        // }
        
        // Wait before retrying
        await this.sleep(Math.min(delay, maxDelay));
        delay *= backoffMultiplier;
      }
    }

    return this.handleResponse(null, lastError, { operation: operationName, userId });
  }

  protected async withPaginatedErrorHandling<T>(
    operation: () => Promise<{ data: T[], pagination?: any }>
  ): Promise<PaginatedResponse<T>> {
    try {
      const result = await operation();
      return this.handlePaginatedResponse(result.data, null, result.pagination);
    } catch (error) {
      return this.handlePaginatedResponse(null, error);
    }
  }

  protected async withTimeoutAndRetry<T>(
    operation: () => Promise<T>,
    timeoutMs: number = 10000,
    retryOptions: RetryOptions = {}
  ): Promise<ServiceResponse<T>> {
    const timeoutOperation = () => Promise.race([
      operation(),
      this.createTimeoutPromise<T>(timeoutMs)
    ]);

    return this.withRetry(timeoutOperation, retryOptions);
  }

  protected async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  protected createTimeoutPromise<T>(timeoutMs: number): Promise<T> {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Operation timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });
  }

  protected isOnline(): boolean {
    return navigator.onLine;
  }

  protected async waitForConnection(maxWaitTime: number = 30000): Promise<boolean> {
    if (this.isOnline()) return true;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        window.removeEventListener('online', onOnline);
        resolve(false);
      }, maxWaitTime);

      const onOnline = () => {
        clearTimeout(timeout);
        window.removeEventListener('online', onOnline);
        resolve(true);
      };

      window.addEventListener('online', onOnline);
    });
  }
}